import { Injectable } from '@angular/core';
import { RECALL_POOLS } from '../sharp-v2.types';
import { RecallItems } from '../sharp-v2.types';
import { SharpScoringService } from './sharp-scoring.service';

@Injectable({ providedIn: 'root' })
export class SharpStateService {
  sessionStart = 0;
  recallItems: RecallItems = { name: '', color: '', place: '', object: '' };
  validityFlags: string[] = [];

  // Step2 change7: material used in the user's earlier scans, so this scan can
  // rotate to fresh material and we can tell real change from a practice effect.
  // priorCategories = word-garden categories, most-recent-first. priorRecallValues
  // = recall-item values from the user's last two scans, per field.
  priorCategories: string[] = [];
  priorRecallValues: Record<keyof RecallItems, string[]> = {
    name: [],
    color: [],
    place: [],
    object: [],
  };

  constructor(private scoring: SharpScoringService) {}

  resetSession(): void {
    this.sessionStart = Date.now();
    this.validityFlags = [];
    for (const k of ['name', 'color', 'place', 'object'] as const) {
      // Step2 change7: avoid values used in the last two scans; pools are large
      // enough (8-10 each) that this always leaves choices.
      const used = this.priorRecallValues[k] || [];
      const pool = RECALL_POOLS[k].filter((v) => !used.includes(v));
      this.recallItems[k] = this.scoring.pick(pool.length ? pool : RECALL_POOLS[k]);
    }
  }

  /** Step2 change7: choose a word-garden category the user has not had yet; once
   *  all three have been used, the least-recently-used one. */
  pickWordCategory(all: string[]): string {
    if (!all.length) return '';
    const unused = all.filter((c) => !this.priorCategories.includes(c));
    if (unused.length) return this.scoring.pick(unused);
    // All used → least recent = the one whose most-recent use is furthest back
    // (largest index in the most-recent-first list).
    let best = all[0];
    let bestRank = -1;
    for (const c of all) {
      const idx = this.priorCategories.indexOf(c);
      const rank = idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
      if (rank > bestRank) {
        bestRank = rank;
        best = c;
      }
    }
    return best;
  }

  /** Step2 change7: seed the rotation from the user's prior cognitive arrays
   *  (most-recent-first). Safe no-op when there is no history (guest / first scan). */
  loadPriorMaterial(priorCognitiveArrays: any[]): void {
    const cats: string[] = [];
    for (const cog of priorCognitiveArrays || []) {
      if (!Array.isArray(cog)) continue;
      const wg = cog.find((t) => t?.testId === 'word-garden');
      if (wg?.response?.category) cats.push(wg.response.category);
    }
    this.priorCategories = cats;

    const vals: Record<keyof RecallItems, string[]> = {
      name: [],
      color: [],
      place: [],
      object: [],
    };
    for (const cog of (priorCognitiveArrays || []).slice(0, 2)) {
      if (!Array.isArray(cog)) continue;
      const rec =
        cog.find((t) => t?.testId === 'recall-encode') ||
        cog.find((t) => t?.testId === 'memory-scene');
      const items = rec?.response?.recallItems;
      if (!items) continue;
      for (const k of ['name', 'color', 'place', 'object'] as const) {
        if (items[k]) vals[k].push(items[k]);
      }
    }
    this.priorRecallValues = vals;
  }

  addValidityFlag(flag: string): void {
    this.validityFlags.push(flag);
  }
}
